/**
 * Heart mask: native Path2D from SVG d (exact curves). Scaled so the path’s bounding box fits
 * `innerSize` like circle/oval — not the full 900×900 viewBox (that left huge empty margin).
 */

import { HEART_DISPLAY_SCALE } from './constants'

/** Original SVG viewBox width/height (reference only). */
export const HEART_VIEWBOX_SIZE = 900

/**
 * Axis-aligned bounds of HEART_PATH_D in SVG user units (tight fit; sampled from the path).
 */
export const HEART_SVG_BBOX = {
  minX: 262.5132,
  minY: 291.6083,
  maxX: 637.4748,
  maxY: 608.0562,
}

const HEART_BBOX_MAX = Math.max(
  HEART_SVG_BBOX.maxX - HEART_SVG_BBOX.minX,
  HEART_SVG_BBOX.maxY - HEART_SVG_BBOX.minY
)

const HEART_BBOX_CX = (HEART_SVG_BBOX.minX + HEART_SVG_BBOX.maxX) / 2
const HEART_BBOX_CY = (HEART_SVG_BBOX.minY + HEART_SVG_BBOX.maxY) / 2

export const HEART_PATH_D =
  'M637.18,370.9c-4.05-56.4-63.12-93.98-118.67-73.86-23.98,8.69-47.08,25.98-68.5,54.83-24.04-29.72-44.52-46.14-68.5-54.83-55.56-20.12-114.12,17.5-118.67,73.86-6.35,78.49,77.96,185.46,180.9,236.39,2.03,1.01,4.42,1.02,6.47.04,107.3-51.1,193.12-150.75,186.97-236.43Z'

function tokenizePathD(s) {
  let str = s.replace(/([MLCZ])/gi, ' $1 ')
  str = str.replace(/,/g, ' ')
  str = str.replace(/([\d.])(-)/g, '$1 $2')
  str = str.replace(/(\d+\.\d+)\.(\d)/g, '$1 .$2')
  str = str.replace(/(\.\d+)(\.\d)/g, '$1 $2')
  return str.trim().split(/\s+/)
}

function cubicBezierPoint(t, x0, y0, x1, y1, x2, y2, x3, y3) {
  const u = 1 - t
  return {
    x: u ** 3 * x0 + 3 * u ** 2 * t * x1 + 3 * u * t ** 2 * x2 + t ** 3 * x3,
    y: u ** 3 * y0 + 3 * u ** 2 * t * y1 + 3 * u * t ** 2 * y2 + t ** 3 * y3,
  }
}

/** Dense samples along the path (for axis geometry only). */
function sampleHeartPathPoints(d, stepsPerCurve = 64) {
  const pts = []
  const tokens = tokenizePathD(d)
  let i = 0
  let x = 0
  let y = 0
  let startX = 0
  let startY = 0
  let repeatCmd = null

  function nextNum() {
    while (i < tokens.length && /^[MLCZ]$/i.test(tokens[i])) i++
    if (i >= tokens.length) return null
    const t = tokens[i]
    if (/^[MLCZ]$/i.test(t)) return null
    i++
    return parseFloat(t)
  }

  while (i < tokens.length) {
    let cmd = tokens[i]
    if (!cmd) break
    if (/^[MLCZ]$/i.test(cmd)) {
      i++
      repeatCmd = null
    } else if (repeatCmd != null && !Number.isNaN(parseFloat(cmd))) {
      cmd = repeatCmd
    } else {
      break
    }

    if (cmd === 'M' || cmd === 'm') {
      const nx = nextNum()
      const ny = nextNum()
      if (nx == null || ny == null) break
      if (cmd === 'm') {
        x += nx
        y += ny
      } else {
        x = nx
        y = ny
      }
      startX = x
      startY = y
      pts.push({ x, y })
    } else if (cmd === 'L' || cmd === 'l') {
      repeatCmd = cmd
      const nx = nextNum()
      const ny = nextNum()
      if (nx == null || ny == null) break
      if (cmd === 'l') {
        x += nx
        y += ny
      } else {
        x = nx
        y = ny
      }
      pts.push({ x, y })
    } else if (cmd === 'C' || cmd === 'c') {
      repeatCmd = cmd
      const x1 = nextNum()
      const y1 = nextNum()
      const x2 = nextNum()
      const y2 = nextNum()
      const x3 = nextNum()
      const y3 = nextNum()
      if (x3 == null || y3 == null) break
      const x0 = x
      const y0 = y
      const ax1 = cmd === 'c' ? x0 + x1 : x1
      const ay1 = cmd === 'c' ? y0 + y1 : y1
      const ax2 = cmd === 'c' ? x0 + x2 : x2
      const ay2 = cmd === 'c' ? y0 + y2 : y2
      const ax3 = cmd === 'c' ? x0 + x3 : x3
      const ay3 = cmd === 'c' ? y0 + y3 : y3
      for (let st = 1; st <= stepsPerCurve; st++) {
        const t = st / stepsPerCurve
        const pt = cubicBezierPoint(t, x0, y0, ax1, ay1, ax2, ay2, ax3, ay3)
        pts.push(pt)
        x = pt.x
        y = pt.y
      }
    } else if (cmd === 'Z' || cmd === 'z') {
      repeatCmd = null
      x = startX
      y = startY
    }
  }
  return pts
}

/**
 * Top cleft (bottom of upper “V”) and bottom tip in SVG space — line through them is the 2-part split axis
 * (slightly tilted if x differs).
 */
function heartSplitAxisSvgFromPoints(pts) {
  let minY = Infinity
  let maxY = -Infinity
  for (const p of pts) {
    minY = Math.min(minY, p.y)
    maxY = Math.max(maxY, p.y)
  }
  const h = maxY - minY || 1
  const bw = HEART_SVG_BBOX.maxX - HEART_SVG_BBOX.minX
  const halfW = bw * 0.18
  const yLo = minY + 0.15 * h
  const yHi = minY + 0.55 * h

  let notch = null
  let bestNotchY = -Infinity
  for (const p of pts) {
    if (p.y < yLo || p.y > yHi) continue
    if (Math.abs(p.x - HEART_BBOX_CX) > halfW) continue
    if (p.y > bestNotchY) {
      bestNotchY = p.y
      notch = p
    }
  }
  if (!notch) {
    notch = { x: HEART_BBOX_CX, y: minY + 0.35 * h }
  }

  let tipY = -Infinity
  let tipX = HEART_BBOX_CX
  for (const p of pts) {
    if (p.y > tipY + 1e-9) {
      tipY = p.y
      tipX = p.x
    } else if (Math.abs(p.y - tipY) <= 1e-9) {
      tipX = (tipX + p.x) / 2
    }
  }

  return { notchX: notch.x, notchY: notch.y, tipX, tipY }
}

const HEART_SPLIT_AXIS_SVG = heartSplitAxisSvgFromPoints(sampleHeartPathPoints(HEART_PATH_D, 64))

function svgHeartPointToCanvas(pt, cx, cy, innerSize) {
  const k = (innerSize / HEART_BBOX_MAX) * HEART_DISPLAY_SCALE
  return {
    x: cx + k * (pt.x - HEART_BBOX_CX),
    y: cy + k * (pt.y - HEART_BBOX_CY),
  }
}

/**
 * Clip to heart ∩ one side of the split line (notch → tip), with optional gap along the axis (same as vertical spacing).
 */
export function clipHeartHalfPlane(ctx, cx, cy, innerSize, keepLeft, spacingPx, canvasSize) {
  const n = svgHeartPointToCanvas(
    { x: HEART_SPLIT_AXIS_SVG.notchX, y: HEART_SPLIT_AXIS_SVG.notchY },
    cx,
    cy,
    innerSize
  )
  const t = svgHeartPointToCanvas({ x: HEART_SPLIT_AXIS_SVG.tipX, y: HEART_SPLIT_AXIS_SVG.tipY }, cx, cy, innerSize)
  const dx = t.x - n.x
  const dy = t.y - n.y
  const L = Math.hypot(dx, dy) || 1
  const nhx = -dy / L
  const nhy = dx / L
  const thx = dx / L
  const thy = dy / L
  const half = Math.max(0, spacingPx) / 2
  const M = canvasSize * 4
  const K = canvasSize * 4

  ctx.beginPath()
  if (keepLeft) {
    const b0x = n.x + half * nhx
    const b0y = n.y + half * nhy
    ctx.moveTo(b0x + M * thx, b0y + M * thy)
    ctx.lineTo(b0x + M * thx + K * nhx, b0y + M * thy + K * nhy)
    ctx.lineTo(b0x - M * thx + K * nhx, b0y - M * thy + K * nhy)
    ctx.lineTo(b0x - M * thx, b0y - M * thy)
  } else {
    const b1x = n.x - half * nhx
    const b1y = n.y - half * nhy
    ctx.moveTo(b1x + M * thx, b1y + M * thy)
    ctx.lineTo(b1x - M * thx, b1y - M * thy)
    ctx.lineTo(b1x - M * thx - K * nhx, b1y - M * thy - K * nhy)
    ctx.lineTo(b1x + M * thx - K * nhx, b1y + M * thy - K * nhy)
  }
  ctx.closePath()
  ctx.clip()
}

let cachedHeartPath2D = null
function getHeartPath2D() {
  if (!cachedHeartPath2D) cachedHeartPath2D = new Path2D(HEART_PATH_D)
  return cachedHeartPath2D
}

/**
 * Map path SVG coords → canvas: bbox max dimension fits `innerSize` (same idea as circle diameter).
 */
function heartTransform(ctx, cx, cy, innerSize) {
  const k = (innerSize / HEART_BBOX_MAX) * HEART_DISPLAY_SCALE
  ctx.setTransform(k, 0, 0, k, cx - k * HEART_BBOX_CX, cy - k * HEART_BBOX_CY)
}

/**
 * Intersect current clip with the heart path (exact curves). Resets CTM to identity after.
 * @param {number} innerSize - s - 2*SHAPE_PADDING, same as circle/oval use for radius/diameter.
 */
export function clipToHeartPath(ctx, cx, cy, innerSize) {
  heartTransform(ctx, cx, cy, innerSize)
  ctx.clip(getHeartPath2D(), 'nonzero')
  ctx.setTransform(1, 0, 0, 1, 0, 0)
}

/**
 * Stroke the heart outline in device pixels (lineWidth not scaled up with the shape).
 */
export function strokeHeartPath(ctx, cx, cy, innerSize, strokeStyle, lineWidth) {
  const k = (innerSize / HEART_BBOX_MAX) * HEART_DISPLAY_SCALE
  ctx.save()
  ctx.strokeStyle = strokeStyle
  ctx.lineWidth = lineWidth / k
  heartTransform(ctx, cx, cy, innerSize)
  ctx.stroke(getHeartPath2D())
  ctx.restore()
}
